// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type MachineElement,
  useEvolveGet,
  type StrongOmit,
  type WithRequiredNotNull,
  type ConduitType,
  type ConduitSize,
  type BendData,
  extractErrorMessage,
  useEvolvePatch,
} from '@evolvemep/api-ts';
import { LoadingOverlay, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { BendCreatorForm } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorForm';
import {
  BendCreatorFormContext,
  bendInputDefaults,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { BendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

type Props = {
  element: MachineElement | null;
  onClose: () => void;
  onBendUpdated: () => void;
};

const getDefaultsFromEditingBend = (
  element: MachineElement,
  bendData: BendData,
  conduitTypes: ConduitType[] | undefined,
  conduitSizes: ConduitSize[] | undefined,
) => {
  const inputDefaults = { ...bendInputDefaults };
  bendData.finalDimensions.forEach(({ keyName, value }) => {
    inputDefaults[keyName] = value;
  });
  const materialStockLength = element.length > 10 ? 20 : 10;
  return {
    benderProfile: element.benderProfile,
    bendType: bendData.bendType,
    conduitTypeId: conduitTypes?.find((ct) => ct.name === element.materialType)?.conduitTypeId ?? null,
    conduitSizeId: conduitSizes?.find((cs) => cs.name === element.materialSize)?.conduitSizeId ?? null,
    materialStockLength,
    maximizeLegLength: element.length === materialStockLength,
    partName: element.name,
    quantity: 1,
    ...inputDefaults,
  };
};

const EditBendModalContents = ({
  element,
  onClose,
  onBendUpdated,
}: StrongOmit<Props, 'element'> & { element: WithRequiredNotNull<MachineElement, 'benderProfile'> }) => {
  const { apiCall: editBend } = useEvolvePatch('shop/fabrication/element/:elementId');
  const { conduitTypes, conduitSizes } = useConduits();
  const {
    data: bendData,
    loading: loadingBendData,
    errors,
  } = useEvolveGet(`shop/fabrication/element/${element.elementId}/bendData`, {
    lazy: false,
  });

  if (isNotNil(errors)) {
    return <Text c="red">{extractErrorMessage(errors)}</Text>;
  }

  if (loadingBendData || isNil(bendData)) {
    return <LoadingOverlay visible />;
  }

  const initialValues = getDefaultsFromEditingBend(element, bendData, conduitTypes, conduitSizes);

  return (
    <BendCreatorFormContext initialValues={initialValues}>
      <BendCreatorValidationContext validateOnMount>
        <BendCreatorForm
          onClose={onClose}
          isEditing
          onSubmit={async ({ benderProfile, ...updatedBendData }) => {
            await editBend(
              {
                bendData: {
                  benderProfileId: benderProfile.machineProfileId,
                  ...updatedBendData,
                },
              },
              {
                url: `shop/fabrication/element/${element.elementId}`,
              },
            );
            notifications.show({
              title: 'Successfully updated bend',
              message: `${element.name} has been updated.`,
              color: 'green',
            });
            onClose();
            onBendUpdated();
          }}
        />
      </BendCreatorValidationContext>
    </BendCreatorFormContext>
  );
};

export const EditBendModal = ({ element, ...rest }: Props) => (
  <VariableTrackingModal
    title={`Edit bend ${element?.name ?? ''}`}
    variable={element}
    onConfirm={rest.onClose}
    onClose={rest.onClose}
    fullScreen
    hideModalButtons
  >
    {(selectedElement) =>
      isNotNil(selectedElement.benderProfile) ? (
        <EditBendModalContents
          element={selectedElement as WithRequiredNotNull<MachineElement, 'benderProfile'>}
          {...rest}
        />
      ) : (
        <Text c="dimmed">This bend does not have an associated profile.</Text>
      )
    }
  </VariableTrackingModal>
);
